import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  type Theme,
} from "@mui/material";
import { inc } from "ramda";
import React, { useContext } from "react";
import { useDrop } from "react-dnd";
import { makeStyles } from "tss-react/mui";

import LessonContext from "../context/LessonContext";
import { ELEMENT_TEMPLATES_TYPE } from "../helpers/const";
import { Spacing } from "../types/enum";
import { type Element, type Maybe } from "../types/graphql";
import Label from "./Label";
import LessonEditPageSectionInstructions from "./LessonEditPageSectionInstructions";
import LessonEditPageSectionSandbox from "./LessonEditPageSectionSandbox";

/**
 * Types
 */
interface Props {
  topicTitle?: string;
  index: number;
  sectionId: string;
  elements?: Maybe<Element[]>;
  topicId: string;
  lessonPath: string;
  step?: number | null;
  total: number;
}

/**
 * Styles
 */
const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    marginBottom: theme.spacing(Spacing.sm),
  },
  content: {
    padding: "unset",
    flexDirection: "column",
  },
}));

const LessonEditPageSection: React.FC<Props> = ({
  index,
  elements,
  sectionId,
  topicTitle,
  topicId,
  lessonPath,
  step,
  total,
}: Props) => {
  const { classes } = useStyles();
  const { onDeleteSection, onSaveSection, onAddElement, onMoveSection } = useContext(LessonContext);

  const [, ref] = useDrop({
    accept: ELEMENT_TEMPLATES_TYPE,
    drop: (item, monitor) => {
      if (monitor.didDrop()) {
        return null;
      }
      onAddElement(sectionId, item);
    },
  });

  const onMoveUp = () => {
    onMoveSection(sectionId, index - 1);
  };

  const onMoveDown = () => {
    onMoveSection(sectionId, index + 1);
  };

  return (
    <Card className={classes.root} ref={ref as unknown as React.Ref<HTMLDivElement>}>
      <CardHeader
        title={`Section #${inc(index)}`}
        subheader={
          <Box mt={Spacing.s} display="flex" justifyContent="space-between" alignItems="center">
            <Label variant="success">{topicTitle}</Label>
            <Box>
              {index ? (
                <IconButton size="small" onClick={onMoveUp}>
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
              ) : null}
              {inc(index) < total ? (
                <IconButton size="small" onClick={onMoveDown}>
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
              ) : null}
            </Box>
          </Box>
        }
      />

      <Divider />

      <CardContent className={classes.content}>
        {elements?.length ? (
          <LessonEditPageSectionSandbox
            elements={elements}
            sectionId={sectionId}
            topicId={topicId}
            lessonPath={lessonPath}
            step={step}
          />
        ) : (
          <LessonEditPageSectionInstructions />
        )}
      </CardContent>

      <Divider />

      <CardActions>
        <Button onClick={() => onDeleteSection(sectionId)}>Remove</Button>
        <Button onClick={() => onSaveSection(sectionId)}>Save</Button>
      </CardActions>
    </Card>
  );
};

export default LessonEditPageSection;
